import type { ActivityType, UserData } from '../../types'
import { getLastSessionText } from '../../utils/entries'
import Dropdown from './Dropdown'
import type { SettingsSection } from '../settings/SettingsModal'

export type DashboardView = 'home' | 'stats' | 'report'

interface NavBarProps {
  data: UserData
  view: DashboardView
  onLog: (type: ActivityType) => void
  onLogFood: () => void
  onSetView: (view: DashboardView) => void
  onOpenSettings: (section: SettingsSection) => void
  onLock?: () => void
}

const LOG_ITEMS: { label: string; color: string; run: 'cardio' | 'weights' | 'exercise' | 'food' }[] = [
  { label: 'Food & Drink', color: 'var(--activity-intake)', run: 'food' },
  { label: 'Cardio', color: 'var(--activity-cardio)', run: 'cardio' },
  { label: 'Weights', color: 'var(--activity-weights)', run: 'weights' },
  { label: 'Exercise', color: 'var(--activity-exercise)', run: 'exercise' },
]

export default function NavBar({ data, view, onLog, onLogFood, onSetView, onOpenSettings, onLock }: NavBarProps) {
  const hasPassword = !!data.passwordHash

  return (
    <header className="sticky top-0 z-40 bg-[var(--bg-secondary)] border-b border-[var(--border)]">
      <div className="max-w-2xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between gap-2 sm:gap-3">
          <button onClick={() => onSetView('home')} className="font-display shrink-0 text-lg sm:text-xl" aria-label="Home">
            <span className="font-extrabold uppercase tracking-tight text-[var(--text-primary)]">WLNS</span>
            <span className="text-[0.8em] font-light text-[var(--text-secondary)]">.app</span>
          </button>

          <button
            onClick={() => onSetView('report')}
            className={`flex-1 min-w-0 truncate text-center text-xs ${
              view === 'report' ? 'text-[var(--accent-teal)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
            title="Open daily report"
          >
            {getLastSessionText(data)}
          </button>

          <div className="flex items-center gap-1 shrink-0">
            <IconButton label="Home" active={view === 'home'} onClick={() => onSetView('home')}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3v-6h6v6h3a1 1 0 001-1V10" />
            </IconButton>

            <Dropdown
              label="Log an entry"
              align="right"
              triggerClassName="p-1.5 sm:p-2 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-card)] transition-colors"
              trigger={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" strokeWidth={2} />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8.5v7M8.5 12h7" />
                </svg>
              }
            >
              {LOG_ITEMS.map((it) => (
                <button
                  key={it.run}
                  className="menu-item"
                  onClick={() => (it.run === 'food' ? onLogFood() : onLog(it.run))}
                >
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: it.color }} />
                  {it.label}
                </button>
              ))}
            </Dropdown>

            <IconButton label="Analytics" active={view === 'stats'} onClick={() => onSetView('stats')}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 19V10m6 9V5m6 14v-6" />
            </IconButton>

            <Dropdown
              label="Settings"
              align="right"
              triggerClassName="p-1.5 sm:p-2 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-card)] transition-colors"
              trigger={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              }
            >
              <button className="menu-item" onClick={() => onOpenSettings('profile')}>Profile</button>
              <button className="menu-item" onClick={() => onOpenSettings('security')}>Security</button>
              <button className="menu-item" onClick={() => onOpenSettings('data')}>Backups &amp; data</button>
              {hasPassword && onLock && (
                <button className="menu-item" onClick={onLock}>Lock now</button>
              )}
            </Dropdown>
          </div>
        </div>
      </div>
    </header>
  )
}

function IconButton({
  children,
  label,
  onClick,
  active,
}: {
  children: React.ReactNode
  label: string
  onClick: () => void
  active: boolean
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
        active ? 'bg-[var(--bg-card)] text-[var(--accent-teal)]' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-card)]'
      }`}
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {children}
      </svg>
    </button>
  )
}
